/**
 * Customer-facing secure Link B URLs.
 *
 * Format: `/p/{readable-ref}/{sequence}/{token}`
 * Example (new): `/p/2608009R-SO-R1/632/7XP4MQ2V`
 * Example (legacy): `/p/2608009R-SO-R1/632/7XP4-MQ2V-N9KR-6CT8-WY3Z-AA`
 *
 * The bearer token is the sole public authorization credential.
 * Numeric legacy paths must never be emitted for customers.
 */

import {
  formatProposalLinkBTokenDisplay,
  isWellFormedProposalLinkBToken,
  normalizeProposalLinkBToken,
} from "./proposalLinkBTokenFormat";
import {
  normalizeProposalLinkBReadableRef,
  toProposalLinkBReadableRef,
} from "./proposalLinkBReadableRef";

export type ProposalSecureLinkBParts = {
  proposalRef: string;
  issuanceSequence: number;
  /** Display or normalized token (hyphens optional). */
  token: string;
};

export type ProposalLinkBFields = {
  id: number;
  projectId?: number | null;
  proposalSource?: string | null;
  originalProposalStorageKey?: string | null;
  /** When present, builds the secure customer path. */
  proposalRef?: string | null;
  issuanceSequence?: number | null;
  token?: string | null;
  secureLinkBPath?: string | null;
};

/** True when a path is a legacy enumerable customer Link B route. */
export function isLegacyNumericProposalLinkBPath(path: string | null | undefined): boolean {
  const p = String(path ?? "").split("?")[0] ?? "";
  return (
    /^\/proposal\/\d+(?:\/approved)?\/?$/i.test(p) ||
    /^\/proposal-manual\/\d+\/?$/i.test(p) ||
    /^\/proposal-wrapper\/\d+\/\d+\/?$/i.test(p)
  );
}

export function formatProposalLinkBSequence(sequence: number): string {
  const n = Math.trunc(Number(sequence));
  if (!Number.isFinite(n) || n <= 0) return "";
  return String(n);
}

export function formatProposalLinkBSequenceLabel(sequence: number): string {
  const n = Math.trunc(Number(sequence));
  if (!Number.isFinite(n) || n <= 0) return "";
  return `Link B #${String(n).padStart(5, "0")}`;
}

export function secureProposalLinkBPath(parts: ProposalSecureLinkBParts): string {
  const readableRef = toProposalLinkBReadableRef(parts.proposalRef);
  const sequence = formatProposalLinkBSequence(parts.issuanceSequence);
  const normalized = normalizeProposalLinkBToken(parts.token);
  if (!readableRef || !sequence || !normalized || !isWellFormedProposalLinkBToken(normalized)) {
    throw new Error("Secure Link B path requires readable ref, sequence, and a valid token.");
  }
  const displayToken = formatProposalLinkBTokenDisplay(normalized);
  return `/p/${encodeURIComponent(readableRef)}/${sequence}/${displayToken}`;
}

export function secureProposalLinkBUrl(parts: ProposalSecureLinkBParts, portalBaseUrl: string): string {
  const base = portalBaseUrl.replace(/\/+$/, "");
  return `${base}${secureProposalLinkBPath(parts)}`;
}

export function secureProposalLinkBApprovedPath(parts: ProposalSecureLinkBParts): string {
  return `${secureProposalLinkBPath(parts)}/approved`;
}

export function parseSecureProposalLinkBPath(path: string): {
  readableRef: string;
  sequence: number;
  token: string;
  approved: boolean;
} | null {
  const p = (String(path ?? "").split("?")[0] ?? "").replace(/\/+$/, "");
  const m = /^\/p\/([^/]+)\/(\d+)\/([^/]+?)(?:\/(approved))?$/i.exec(p);
  if (!m) return null;
  const readableRef = normalizeProposalLinkBReadableRef(decodeURIComponent(m[1] ?? ""));
  const sequence = Number(m[2]);
  const token = normalizeProposalLinkBToken(decodeURIComponent(m[3] ?? ""));
  if (
    !readableRef ||
    !Number.isFinite(sequence) ||
    sequence <= 0 ||
    !token ||
    !isWellFormedProposalLinkBToken(token)
  ) {
    return null;
  }
  return {
    readableRef,
    sequence,
    token,
    approved: Boolean(m[4]),
  };
}

/**
 * Customer Link B path. Requires secure credential fields — never emits numeric IDs.
 * Prefer `secureLinkBPath` when the server already resolved the authoritative URL.
 */
export function proposalLinkBPath(proposal: ProposalLinkBFields): string {
  const preexisting = String(proposal.secureLinkBPath ?? "").trim();
  if (preexisting.startsWith("/p/")) return preexisting.split("?")[0]!;

  const sequence = Number(proposal.issuanceSequence ?? 0);
  const token = String(proposal.token ?? "").trim();
  const proposalRef = String(proposal.proposalRef ?? "").trim();
  if (proposalRef && Number.isFinite(sequence) && sequence > 0 && token) {
    return secureProposalLinkBPath({
      proposalRef,
      issuanceSequence: sequence,
      token,
    });
  }

  throw new Error(
    "Secure Link B credential required. Customer URLs must use /p/{readable-ref}/{sequence}/{token}.",
  );
}

export function proposalLinkBUrl(proposal: ProposalLinkBFields, portalBaseUrl: string): string {
  const base = portalBaseUrl.replace(/\/+$/, "");
  return `${base}${proposalLinkBPath(proposal)}`;
}

/** Internal renderer kind after credential verification (not exposed in customer URLs). */
export type ProposalLinkBRendererKind =
  | "retail"
  | "dtools"
  | "manual_pdf"
  | "project_wrapper";
